import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

import { API_SERVICE, type CustosApi } from '../api/api.provider';
import type { ApiMarket } from '../api/api.types';

/**
 * Shared cache for getMarkets() calls.
 * Prevents duplicate HTTP requests when multiple stores load concurrently.
 */
@Injectable({ providedIn: 'root' })
export class MarketCacheService {
  private readonly api = inject<CustosApi>(API_SERVICE);
  private cache$: Observable<ApiMarket[]> | null = null;
  private cacheTimestamp = 0;
  private static readonly CACHE_MS = 2 * 60 * 1000;

  /** Returns a shared, cached observable of markets. */
  getMarkets(): Observable<ApiMarket[]> {
    if (this.cache$ && Date.now() - this.cacheTimestamp < MarketCacheService.CACHE_MS) {
      return this.cache$;
    }
    this.cache$ = this.api.getMarkets().pipe(
      tap({
        error: () => {
          // On error, invalidate cache so next caller retries instead of replaying the error
          this.cache$ = null;
          this.cacheTimestamp = 0;
        },
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    // Set timestamp AFTER creating the observable (not before)
    this.cacheTimestamp = Date.now();
    return this.cache$;
  }

  /** Force-invalidate the cache (e.g., after a mutation). */
  invalidate(): void {
    this.cache$ = null;
    this.cacheTimestamp = 0;
  }
}
